import { Texture } from 'three'
import { HardwareProps } from './../classes/Hardware'
import { cabinetAPI, dtoolAPI } from './axiosInstances'
import { HardwareTypes } from '../classes/Hardware'
import { CompanyType, RoleType } from '../redux/appReducer'

// Hardwares

export type HardwareType = {
  id: number
  date: string
  name: string
  sku: string
  description: string
  type: HardwareTypes
  unitWeight: number
  unitPrice: number
  unitPriceAddon: number
  addonD: number
  addonP: number
  minPrice: number
  markupId: number
  materialsIds: string[]
  defaultMaterialId: number
  childrenIds: number[]
  editable: string //0 or 1
}

export const fetchHardwares = () =>
  cabinetAPI
    .get<Array<HardwareType>>(`index.php`, { params: { q: `hardwares` } })
    .then((res) => res.data)

export const fetchHardwareByID = (id: number) =>
  cabinetAPI
    .get<HardwareType>(`index.php`, { params: { q: `hardwares/${id}` } })
    .then((res) => res.data)

export const addHardwareToDB = (hardware: HardwareProps) =>
  cabinetAPI
    .post<string>(`index.php`, hardware, {
      params: { q: `hardwares` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const editHardwareInDB = (hardware: HardwareType) =>
  cabinetAPI
    .put<HardwareType>(`index.php`, hardware, {
      params: { q: `hardwares/${hardware.id}` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const deleteHardwareFromDB = (id: number) =>
  cabinetAPI
    .delete<string>(`index.php`, { params: { q: `hardwares/${id}` } })
    .then((res) => res.data)

// Markups

export type MarkupType = {
  id: number
  name: string
  value: number
}

export type MarkupPropsType = {
  name: string
  value: number
}

export const fetchMarkups = () =>
  cabinetAPI
    .get<Array<MarkupType>>(`index.php`, { params: { q: `markups` } })
    .then((res) => res.data)

export const addMarkupToDB = (markup: MarkupPropsType) =>
  cabinetAPI
    .post<string>(`index.php`, markup, {
      params: { q: `markups` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const editMarkupInDB = (id: number, markup: MarkupPropsType) =>
  cabinetAPI
    .put<MarkupType>(`index.php`, markup, {
      params: { q: `markups/${id}` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

// Materials

export type MaterialType = {
  id: number
  date: string
  name: string
  displayName: string
  sku: string //unique
  type: string
  cabinetDoor: string //00000
  thumbnail: string
  texture: { url: string; texture?: Texture }
  length: number
  width: number
  description: string
  weight: number
  price: number
  addon: number
  permissions: string //000
  editable: string //0 or 1
  distributorCompany: string
  threeProps: string
}

export const materialTypeValues = [
  'powder',
  'melamine ',
  'ctopLinear',
  'ctopSquare',
  'floor',
]

export type MaterialPropsType = {
  name: string
  displayName: string
  sku: string
  type: string
  cabinetDoor: string //00000
  thumbnail?: string
  texture?: string
  length: number
  width: number
  description?: string
  weight: number
  price: number
  addon: number
  permissions: string //000
  editable: string //0 or 1
  distributorCompany: string
  threeProps: string
}

export type MaterialAPIType = {
  id: number
  date: string
  name: string
  displayName: string
  sku: string
  type: string
  cabinetDoor: string //00000
  thumbnail: string
  texture: string
  length: number
  width: number
  description: string
  weight: number
  price: number
  addon: number
  permissions: string //000
  editable: string //0 or 1
  distributorCompany: string
  threeProps: string
}

export const fetchMaterials = () =>
  cabinetAPI
    .get<MaterialAPIType[]>(`index.php`, { params: { q: `materials` } })
    .then((res) => res.data)

export const addMaterialToDB = (material: MaterialPropsType) =>
  cabinetAPI
    .post<string>(`index.php`, material, {
      params: { q: `materials` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const editMaterialInDB = (id: number, material: MaterialPropsType) =>
  cabinetAPI
    .put<HardwareType>(`index.php`, material, {
      params: { q: `materials/${id}` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const deleteMaterialFromDB = (id: number) =>
  cabinetAPI
    .delete<string>(`index.php`, { params: { q: `materials/${id}` } })
    .then((res) => res.data)

export const uploadTextureToServer = (name: string, newTexture: string) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      {
        name,
        newTexture,
      },
      {
        params: { q: `textures` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const fetchTextures = () =>
  cabinetAPI
    .get<string[]>(`index.php`, { params: { q: `textures/` } })
    .then((res) => res.data)

// Material Groups

export type MaterialGroupType = {
  id: number
  date: string
  name: string
  description: string
  materialsIds: number[]
}

export type MaterialGroupPropsType = {
  name: string
  description: string
  materialsIds: number[]
}

export const fetchMaterialGroups = () =>
  cabinetAPI
    .get<MaterialGroupType[]>(`index.php`, { params: { q: `materialGroups` } })
    .then((res) => res.data)

export const addMaterialGroupToDB = (materialGroup: MaterialGroupPropsType) =>
  cabinetAPI
    .post<string>(`index.php`, materialGroup, {
      params: { q: `materialGroups` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const editMaterialGroupInDB = (
  id: number,
  materialGroup: MaterialGroupPropsType
) =>
  cabinetAPI
    .put<MaterialGroupType>(`index.php`, materialGroup, {
      params: { q: `materialGroups/${id}` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const deleteMaterialGroupFromDB = (id: number) =>
  cabinetAPI
    .delete<string>(`index.php`, { params: { q: `materialGroups/${id}` } })
    .then((res) => res.data)

//auth

export type UserType = {
  name: string
  lastName: string
  phone: string
  email: string
  role: RoleType
  cabinetsIds: number[]
  offerMarkups: number[]
  companyName: string
  companyLogo: string
  refEmail: string | undefined
  ghlUrl: string
  options: string
}

export const me = (token: string) =>
  cabinetAPI
    .post<UserType>(
      `index.php`,
      { token },
      {
        params: { q: `users/me` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editUserAPI = (token: string, user: UserType) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      {
        ...user,
        token,
        name: user.name,
        lastName: user.lastName,
        phone: user.phone,
        companyName: user.companyName,
        offerMarkups: user.offerMarkups.join(';'),
        companyLogo: user.companyLogo,
        options: user.options,
      },
      {
        params: { q: `users` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const logoutAPI = (token: string) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { token },
      {
        params: { q: `users/logout` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const getToken = (email: string, password: string) =>
  cabinetAPI
    .post<{ token: string; user: UserType }>(
      `index.php`,
      { email, password },
      {
        params: { q: `users/login` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const createUser = (
  name: string,
  email: string,
  password: string,
  refEmail: string | undefined
) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { name, email, password, refEmail },
      {
        params: { q: `users` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const generateRecoveryCodeApi = (
  userEmail: string,
  token: string,
  code: string
) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { userEmail, token, code },
      {
        params: { q: `users/recoverPassword` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export type RecoveryCodeType = {
  userEmail: string
  code: string
  refEmail: string
}

export const fetchRecoveryCodes = (token: string) =>
  cabinetAPI
    .post<RecoveryCodeType[]>(
      `index.php`,
      { token },
      {
        params: { q: `getRecoveryCodes` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const resetPasswordApi = (code: string, password: string) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { code, password },
      {
        params: { q: `users/recoverPassword` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const fetchCompanyInfo = (email: string) =>
  cabinetAPI
    .get<CompanyType>(`index.php`, { params: { q: 'company/' + email } })
    .then((res) => res.data)

export type UserInfoType = {
  id: number
  email: string
  name: string
  lastName: string
  phone: string
  role: RoleType
  companyName: string
  offerMarkups: number[]
  refEmail: string
  ghlUrl: string
  options: string
}

export const fetchUsersInfo = (token: string) =>
  cabinetAPI
    .post<UserInfoType[]>(
      `index.php`,
      { token },
      {
        params: { q: `usersInfo` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const fetchCompanyUsersInfo = (token: string) =>
  cabinetAPI
    .post<UserInfoType[]>(
      `index.php`,
      { token },
      {
        params: { q: `companyUsersInfo` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const fetchCompanyUsersEmails = (token: string) =>
  cabinetAPI
    .post<string[]>(
      `index.php`,
      { token },
      {
        params: { q: `companyEmails` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const setUserRoleAPI = (token: string, email: string, role: RoleType) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { token, email, role },
      {
        params: { q: `usersRole` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const setUserFieldAPI = (
  token: string,
  email: string,
  fieldName: string,
  fieldValue: string
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { token, email, fieldName, fieldValue },
      {
        params: { q: `usersField` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editCompanyMarkupsAPI = (
  token: string,
  companyEmail: string,
  offerMarkups: string
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { token, companyEmail, offerMarkups },
      {
        params: { q: `companyMarkups` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

// export type CompanyOptionsType = {
//     optBuyButton: boolean[]
// }

export const editCompanyOptionsAPI = (
  token: string,
  companyEmail: string,
  options: string
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      {
        token,
        companyEmail,
        options,
      },
      {
        params: { q: `companyOptions` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const fetchConfigs = () =>
  cabinetAPI
    .get<{ name: string; value: string; description: string }[]>(`index.php`, {
      params: { q: `configs` },
    })
    .then((res) => res.data)

export type CabinetSizesSubType = {
  width: { default: number; values: number[] }
  height: { default: number; values: number[] }
  depth: { default: number; values: number[] }
}

export type CornerCabinetSizesSubType = {
  height: { default: number; values: number[] }
  width1: { default: number; values: number[] }
  width2: { default: number; values: number[] }
  backWidth1: { default: number; values: number[] }
  backWidth2: { default: number; values: number[] }
  depth1: { default: number; values: number[] }
  depth2: { default: number; values: number[] }
}

export type CabinetSizesType = {
  base_retail_cabinet_sizes: CabinetSizesSubType
  tall_retail_cabinet_sizes: CabinetSizesSubType
  upper_retail_cabinet_sizes: CabinetSizesSubType
  base_dealer_cabinet_sizes: CabinetSizesSubType
  tall_dealer_cabinet_sizes: CabinetSizesSubType
  upper_dealer_cabinet_sizes: CabinetSizesSubType
  base_designer_cabinet_sizes: CabinetSizesSubType
  tall_designer_cabinet_sizes: CabinetSizesSubType
  upper_designer_cabinet_sizes: CabinetSizesSubType
}

export type CornerCabinetSizesType = {
  base_retail_cornerCabinet_sizes: CornerCabinetSizesSubType
  tall_retail_cornerCabinet_sizes: CornerCabinetSizesSubType
  upper_retail_cornerCabinet_sizes: CornerCabinetSizesSubType
  base_dealer_cornerCabinet_sizes: CornerCabinetSizesSubType
  tall_dealer_cornerCabinet_sizes: CornerCabinetSizesSubType
  upper_dealer_cornerCabinet_sizes: CornerCabinetSizesSubType
  base_designer_cornerCabinet_sizes: CornerCabinetSizesSubType
  tall_designer_cornerCabinet_sizes: CornerCabinetSizesSubType
  upper_designer_cornerCabinet_sizes: CornerCabinetSizesSubType
}

export const fetchCabinetSizes = () =>
  cabinetAPI
    .get<{ name: string; value: string }[]>(`index.php`, {
      params: { q: `appConfigs` },
    })
    .then((res) => res.data)

export const editConfigInDB = (
  name: string,
  value: string,
  description: string,
  token: string
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { token, name, value, description },
      {
        params: { q: `configs` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editCabinetSizesInDB = (
  name: string,
  value: string,
  token: string
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { token, name, value },
      {
        params: { q: `appConfig` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export type CabType = {
  //Cab is instance of 'cabinets' table in db
  id: number
  date: string
  title: string
  name: string
  additional: string
  image: string
  creator?: string
}

export type CabPropsType = {
  title: string
  name: string
  additional: string
  image: string
}

export const fetchUserCabinets = (token: string) =>
  cabinetAPI
    .post<CabType[]>(
      `index.php`,
      { token },
      {
        params: { q: `userCabinets` },
      }
    )
    .then((res) => res.data)

export const fetchAllCabinets = (token: string) =>
  cabinetAPI
    .post<CabType[]>(
      `index.php`,
      { token },
      {
        params: { q: `allCabinets` },
      }
    )
    .then((res) => res.data)

export const addCabinetToDB = (cabinet: CabPropsType, token: string) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { ...cabinet, token },
      {
        params: { q: `cabinets` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editCabinetInDB = (id: number, cabinet: CabPropsType) =>
  cabinetAPI
    .put<MaterialGroupType>(`index.php`, cabinet, {
      params: { q: `cabinets/${id}` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const deleteCabinetFromDB = (id: number) =>
  cabinetAPI
    .delete<MaterialGroupType>(`index.php`, { params: { q: `cabinets/${id}` } })
    .then((res) => res.data)

export const shareCabinetInDB = (
  cabinetId: number,
  isReference: boolean,
  emails: string[]
) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { cabinetId, isReference, emails },
      {
        params: { q: `shareCabinet` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export type CategoryType = {
  id: number
  date: string
  name: string
  displayName: string
  forCorner: boolean
  retail: CabinetSizesSubType | CornerCabinetSizesSubType
  dealer: CabinetSizesSubType | CornerCabinetSizesSubType
  designer: CabinetSizesSubType | CornerCabinetSizesSubType
}

export type CategoryPropsType = {
  name: string
  displayName: string
  forCorner: boolean
  retail: CabinetSizesSubType | CornerCabinetSizesSubType
  dealer: CabinetSizesSubType | CornerCabinetSizesSubType
  designer: CabinetSizesSubType | CornerCabinetSizesSubType
}

export type CategoryPropsAPIType = {
  name: string
  displayName: string
  forCorner: '0' | '1'
  retail: string
  dealer: string
  designer: string
}

export type CategoryAPIType = {
  id: number
  date: string
  forCorner: '0' | '1'
  name: string
  displayName: string
  retail: string
  dealer: string
  designer: string
}

export const fetchCategories = () =>
  cabinetAPI
    .get<CategoryAPIType[]>(`index.php`, { params: { q: `categories` } })
    .then((res) => res.data)

export const addCategoryToDB = (
  categoryProps: CategoryPropsAPIType,
  token: string
) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { ...categoryProps, token },
      {
        params: { q: `categories` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editCategoryInDB = (
  id: number,
  cabinet: CategoryPropsAPIType,
  token: string
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { ...cabinet, token },
      {
        params: { q: `categories/${id}` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const deleteCategoryFromDB = (id: number) =>
  cabinetAPI
    .delete<string>(`index.php`, { params: { q: `categories/${id}` } })
    .then((res) => res.data)

//Costumers

export type CostumerPropsType = {
  companyEmail: string
  creatorEmail: string
  firstName: string
  lastName: string
  email: string
  status: string
  phone: string
  zip: string
  address1: string
  address2: string
  city: string
  province: string
  country: string
  note: string
}

export interface CostumerType extends CostumerPropsType {
  id: number
  date: string
}

export const fetchAllCostumers = (token: string) =>
  cabinetAPI
    .post<
      CostumerType[]
    >(`index.php`, { token }, { params: { q: `getAllCostumers` } })
    .then((res) => res.data)

export const fetchCostumers = (companyEmail: string) =>
  cabinetAPI
    .post<
      CostumerType[]
    >(`index.php`, { companyEmail }, { params: { q: `getCostumers` } })
    .then((res) => res.data)

export const addCostumerToDB = (costumerProps: CostumerPropsType) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { ...costumerProps },
      {
        params: { q: `costumers` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editCostumerInDB = (
  id: number,
  costumerProps: CostumerPropsType
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { ...costumerProps },
      {
        params: { q: `costumers/${id}` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const deleteCostumerFromDB = (id: number) =>
  cabinetAPI
    .delete<string>(`index.php`, { params: { q: `costumers/${id}` } })
    .then((res) => res.data)

export const fetchCostumerHash = (email: string, appId: number) =>
  dtoolAPI
    .get<string>(`getUserLastHashByEmail.php`, { params: { email, appId } })
    .then((res) => res.data)

//Floor Materials

export type FloorMaterialPropsType = {
  name: string
  sku: string
  materialId: number
  dealerId: string
  floorSetupCharge: number
  floorPricePerSqFt: number
  curbSetupCharge: number
  curbPricePerSqFt: number
  curbLinearPricePerFt: number
  shortDescription: string
  longDescription: string
  pictureUrl: string
  pdfUrl: string
}

export interface FloorMaterialType extends FloorMaterialPropsType {
  id: number
  date: string
}

export const fetchFloorMaterials = () =>
  cabinetAPI
    .get<FloorMaterialType[]>(`index.php`, { params: { q: `floorMaterials` } })
    .then((res) => res.data)

export const addFloorMaterialToDB = (fmProps: FloorMaterialPropsType) =>
  cabinetAPI
    .post<string>(
      `index.php`,
      { ...fmProps },
      {
        params: { q: `floorMaterials` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

export const editFloorMaterialInDB = (
  id: number,
  fmProps: FloorMaterialPropsType
) =>
  cabinetAPI
    .put<string>(
      `index.php`,
      { ...fmProps },
      {
        params: { q: `floorMaterials/${id}` },
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    )
    .then((res) => res.data)

// Discount Tiers

export type DiscountTierType = {
  id: number
  date: string
  name: string
  extraDiscountStartPoint: number
  PPFD_OfferCabinets: number[]
  DPA_OfferCabinets: number[]
  PPFD_OfferSlatwallAndAcc: number[]
  DPA_OfferSlatwallAndAcc: number[]
  PPFD_OfferOverheadStorage: number[]
  DPA_OfferOverheadStorage: number[]
}

export type DiscountTierPropsType = {
  name: string
  extraDiscountStartPoint: number
  DPA_OfferCabinets: number[]
  PPFD_OfferCabinets: number[]
  PPFD_OfferSlatwallAndAcc: number[]
  DPA_OfferSlatwallAndAcc: number[]
  PPFD_OfferOverheadStorage: number[]
  DPA_OfferOverheadStorage: number[]
}

export const fetchDiscountTiers = () =>
  cabinetAPI
    .get<Array<DiscountTierType>>(`index.php`, {
      params: { q: `discountTiers` },
    })
    .then((res) => res.data)

export const addDiscountTierToDB = (tier: DiscountTierPropsType) =>
  cabinetAPI
    .post<string>(`index.php`, tier, {
      params: { q: `discountTiers` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const editDiscountTierInDB = (id: number, tier: DiscountTierPropsType) =>
  cabinetAPI
    .put<MarkupType>(`index.php`, tier, {
      params: { q: `discountTiers/${id}` },
      headers: {
        'Content-Type': 'text/plain',
      },
    })
    .then((res) => res.data)

export const deleteDiscountTierFromDB = (id: number) =>
  cabinetAPI
    .delete<string>(`index.php`, { params: { q: `discountTiers/${id}` } })
    .then((res) => res.data)

export const saveBackupAPI = () =>
  cabinetAPI.get(`backup.php`).then((res) => res.data)
